/** Боковая панель карточки «WB: регионы» (оффер, поставка): модальная, фокус внутри, Esc и
 *  клик по фону закрывают; исключение рендера содержимого — текстом в панели, страница жива. */
import { X } from "lucide-react";
import React, { Component, useEffect, useId, useRef } from "react";

import { useDialogFocus } from "../ozon-analytics/useDialogFocus";

class CardErrorBoundary extends Component<
  { what: string; children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(`${this.props.what}: ошибка рендера`, error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          Ошибка отображения карточки: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

export function SidePanel({
  title,
  testId,
  resetKey,
  onClose,
  children,
}: {
  /** подпись панели («Карточка оффера WB») — она же доступное имя диалога */
  title: string;
  testId: string;
  /** смена ключа сбрасывает ошибку рендера (другой объект) */
  resetKey: string;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(panelRef, closeRef, onClose);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" onClick={onClose} aria-hidden data-testid="card-backdrop" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        className="absolute inset-y-0 right-0 flex w-[min(880px,100vw)] flex-col border-l bg-background text-foreground shadow-xl duration-150 animate-in slide-in-from-right"
      >
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <span id={titleId} className="text-xs uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Закрыть карточку"
            className="ml-auto rounded p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain p-4">
          <CardErrorBoundary key={resetKey} what={title}>
            {children}
          </CardErrorBoundary>
        </div>
      </div>
    </div>
  );
}
